import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Grid,
  Autocomplete,
  TextField,
  Collapse,
  Button,
} from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GradeIcon from "@mui/icons-material/Grade";
import LabelIcon from "@mui/icons-material/Label";
import StatsCards from "./StatsCards";

const PeopleCards = ({ people, search, setSearch, onPersonClick, isLoading }) => {
  const [filterDepartment, setFilterDepartment] = useState([]);
  const [filterGrade, setFilterGrade] = useState([]);
  const [filterLevel, setFilterLevel] = useState([]);
  const [filterRole, setFilterRole] = useState([]);
  const [filterEmpType, setFilterEmpType] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  // Extract unique values for filters
  const departments = useMemo(
    () => [...new Set(people.map((p) => p.dep_name).filter(Boolean))].sort(),
    [people]
  );
  const grades = useMemo(
    () => [...new Set(people.map((p) => p.grade_id).filter(Boolean))].sort((a, b) => a - b),
    [people]
  );
  const levels = useMemo(
    () => [...new Set(people.map((p) => p.level_name).filter(Boolean))].sort(),
    [people]
  );
  const roles = useMemo(
    () => [...new Set(people.map((p) => p.role_name).filter(Boolean))].sort(),
    [people]
  );
  const empTypes = useMemo(
    () => [...new Set(people.map((p) => p.empTypeName).filter(Boolean))].sort(),
    [people]
  );

  const filteredPeople = people.filter((person) => {
    const matchesSearch = person.name?.toLowerCase().includes(search.toLowerCase());
    const matchesDepartment = filterDepartment.length === 0 || filterDepartment.includes(person.dep_name);
    const matchesGrade = filterGrade.length === 0 || filterGrade.includes(person.grade_id);
    const matchesLevel = filterLevel.length === 0 || filterLevel.includes(person.level_name);
    const matchesRole = filterRole.length === 0 || filterRole.includes(person.role_name);
    const matchesEmpType = filterEmpType.length === 0 || filterEmpType.includes(person.empTypeName);

    return matchesSearch && matchesDepartment && matchesGrade && matchesLevel && matchesRole && matchesEmpType;
  });

  const clearAllFilters = () => {
    setFilterDepartment([]);
    setFilterGrade([]);
    setFilterLevel([]);
    setFilterRole([]);
    setFilterEmpType([]);
    setSearch("");
  };

  const activeFiltersCount = filterDepartment.length + filterGrade.length + filterLevel.length + filterRole.length + filterEmpType.length + (search ? 1 : 0);

  const removeFilter = (type, value) => {
    switch (type) {
      case "department":
        setFilterDepartment(filterDepartment.filter((v) => v !== value));
        break;
      case "grade":
        setFilterGrade(filterGrade.filter((v) => v !== value));
        break;
      case "level":
        setFilterLevel(filterLevel.filter((v) => v !== value));
        break;
      case "role":
        setFilterRole(filterRole.filter((v) => v !== value));
        break;
      case "empType":
        setFilterEmpType(filterEmpType.filter((v) => v !== value));
        break;
      case "search":
        setSearch("");
        break;
      default:
        break;
    }
  };

  return (
    <div>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 4,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress
            color="success"
            size={40}
            thickness={4}
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            Loading people data...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary Stats */}
          <StatsCards people={people} />

          {/* Search and Filters */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: "16px",
              background: "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
              border: "1px solid #e0e0e0",
            }}
          >
            {/* Filter Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FilterListIcon
                  sx={{ mr: 1.5, color: "#228B22", fontSize: "1.8rem" }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                  Search & Filter
                </Typography>
                {activeFiltersCount > 0 && (
                  <Chip
                    label={`${activeFiltersCount} active`}
                    size="small"
                    sx={{
                      ml: 2,
                      backgroundColor: "#228B22",
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {activeFiltersCount > 0 && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearAllFilters}
                    sx={{
                      color: "#d32f2f",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "#ffebee" },
                    }}
                  >
                    Clear All
                  </Button>
                )}
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    color: "#228B22",
                    "&:hover": { backgroundColor: "#e8f5e9" },
                  }}
                >
                  {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            {/* Active Filter Chips */}
            {activeFiltersCount > 0 && (
              <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {search && (
                  <Chip
                    icon={<SearchIcon />}
                    label={`Search: "${search}"`}
                    onDelete={() => removeFilter("search")}
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                )}
                {filterDepartment.map((dept) => (
                  <Chip
                    key={dept}
                    icon={<BusinessIcon />}
                    label={dept}
                    onDelete={() => removeFilter("department", dept)}
                    sx={{
                      backgroundColor: "#e3f2fd",
                      borderColor: "#2196f3",
                      color: "#1565c0",
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {filterGrade.map((grade) => (
                  <Chip
                    key={grade}
                    icon={<GradeIcon />}
                    label={`Grade ${grade}`}
                    onDelete={() => removeFilter("grade", grade)}
                    sx={{
                      backgroundColor: "#fff3e0",
                      borderColor: "#ff9800",
                      color: "#e65100",
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {filterLevel.map((level) => (
                  <Chip
                    key={level}
                    icon={<LabelIcon />}
                    label={level}
                    onDelete={() => removeFilter("level", level)}
                    sx={{
                      backgroundColor: "#f3e5f5",
                      borderColor: "#9c27b0",
                      color: "#6a1b9a",
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {filterRole.map((role) => (
                  <Chip
                    key={role}
                    icon={<WorkIcon />}
                    label={role}
                    onDelete={() => removeFilter("role", role)}
                    sx={{
                      backgroundColor: "#e8f5e9",
                      borderColor: "#228B22",
                      color: "#1b5e20",
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
                {filterEmpType.map((type) => (
                  <Chip
                    key={type}
                    icon={<BadgeIcon />}
                    label={type}
                    onDelete={() => removeFilter("empType", type)}
                    sx={{
                      backgroundColor: "#fce4ec",
                      borderColor: "#e91e63",
                      color: "#880e4f",
                    }}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            )}

            <Collapse in={showFilters}>
              {/* Search Bar */}
              <TextField
                fullWidth
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "#666" }} />,
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                }}
              />

              {/* Filter Autocompletes */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={departments}
                    value={filterDepartment}
                    onChange={(event, newValue) => setFilterDepartment(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department"
                        placeholder="Select..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option}
                          size="small"
                          color="primary"
                        />
                      ))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={grades}
                    value={filterGrade}
                    onChange={(event, newValue) => setFilterGrade(newValue)}
                    getOptionLabel={(option) => `Grade ${option}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Grade"
                        placeholder="Select..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={`G${option}`}
                          size="small"
                          sx={{
                            backgroundColor: "#ff9800",
                            color: "white",
                          }}
                        />
                      ))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={levels}
                    value={filterLevel}
                    onChange={(event, newValue) => setFilterLevel(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Level"
                        placeholder="Select..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: "#9c27b0",
                            color: "white",
                          }}
                        />
                      ))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={roles}
                    value={filterRole}
                    onChange={(event, newValue) => setFilterRole(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Role"
                        placeholder="Select..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: "#228B22",
                            color: "white",
                          }}
                        />
                      ))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={empTypes}
                    value={filterEmpType}
                    onChange={(event, newValue) => setFilterEmpType(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employment Type"
                        placeholder="Select..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: "#e91e63",
                            color: "white",
                          }}
                        />
                      ))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Results Count */}
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Showing <strong>{filteredPeople.length}</strong> of{" "}
                  <strong>{people.length}</strong> people
                </Typography>
              </Box>
            </Collapse>
          </Paper>

          {/* People Cards Grid */}
          <div className="people-grid">
            {filteredPeople.map((person) => (
              <Paper
                key={person.id || person.staff_id}
                elevation={2}
                className="person-card"
                onClick={() => onPersonClick(person)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {/* Card header with fixed height */}
                <div
                  className="card-header"
                  style={{ backgroundColor: "#228B22" }}
                ></div>

                {/* Card avatar with fixed position */}
                <div className="card-avatar">
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      border: "4px solid white",
                      fontSize: "2rem",
                      backgroundColor: "#228B22",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {person.name ? person.name.charAt(0).toUpperCase() : "?"}
                  </Avatar>
                </div>

                {/* Card content with consistent padding */}
                <div className="card-content">
                  <Typography className="card-name">{person.name}</Typography>

                  <Typography className="card-id">
                    <BadgeIcon sx={{ fontSize: "0.9rem", mr: 0.5 }} />
                    {person.staff_id}
                    <Tooltip title="Copy UID">
                      <IconButton
                        size="small"
                        className="copy-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(person.staff_id);
                        }}
                        sx={{
                          ml: 0.5,
                          p: 0.3,
                          visibility: "hidden",
                          opacity: 0,
                          transition: "visibility 0s, opacity 0.2s linear",
                        }}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Typography>

                  <Typography className="card-role">
                    <WorkIcon
                      sx={{ fontSize: "1rem", mr: 0.5, color: "#228B22" }}
                    />
                    {person.role_name || "Not assigned"}
                  </Typography>

                  <div className="card-level-grade">
                    <div className="card-level">{person.level || "No Level"}</div>
                    <div className="card-grade">
                      {person.grade_id ? `Grade ${person.grade_id}` : "No Grade"}
                    </div>
                  </div>

                  <div className="card-info-row">
                    <BusinessIcon
                      sx={{ fontSize: "0.9rem", mr: 0.5, color: "#666" }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {person.dep_name || "Not assigned"}
                    </span>
                  </div>

                  <div className="card-info-row">
                    <LocationOnIcon
                      sx={{ fontSize: "0.9rem", mr: 0.5, color: "#666" }}
                    />
                    <span>{person.res_country || "Not specified"}</span>
                  </div>

                  {/* Card footer - pushed to bottom */}
                  <div className="card-footer">
                    <span className="contact-label">Contact via:</span>
                    <div className="email-container">
                      <a
                        href={`mailto:${person.per_email || person.login_id}`}
                        className="contact-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EmailIcon
                          sx={{ fontSize: "1rem", mr: 0.5, flexShrink: 0 }}
                        />
                        <span className="email-text">
                          {person.per_email || person.login_id}
                        </span>
                      </a>
                      <Tooltip title="Copy Email">
                        <IconButton
                          size="small"
                          className="copy-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(
                              person.per_email || person.login_id
                            );
                          }}
                          sx={{
                            ml: 0.5,
                            p: 0.3,
                            visibility: "hidden",
                            opacity: 0,
                            transition: "visibility 0s, opacity 0.2s linear",
                          }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </Paper>
            ))}
          </div>
          {filteredPeople.length === 0 && !isLoading && (
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Typography variant="h6" sx={{ color: "text.secondary" }}>
                No people match your search
              </Typography>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default PeopleCards;
